import { useContext } from "react";

import { UserContext } from "../../../context/UserContext";
import api from "../../../services/api";
import { CustomerContext } from "../contexts/CustomerContext";
import { notify } from "../../../utils/notify";
import validatesNotNull from "../../../utils/validatesNotNull";
import { GlobalVariables } from "../../../global";
import { postLog } from "../../../repositories/v1/log";
import usePromocodeController from "./PromocodeController";

export default function useProductController() {
  const { user } = useContext(UserContext);
  const {
    fantasy,
    cnpj,
    email,
    products,
    setProducts,
    optionsVersions,
    setErrors,
    setLoadings,
    franchisedCompany,
  } = useContext(CustomerContext);

  const { questionDefaultFranchise, handleSavePromocode } =
    usePromocodeController();

  const handleAddProduct = async (generateBase) => {
    try {
      if (!franchisedCompany) {
        const changeFranchiseToDefault = await boolChangeToDefaultFranchise();

        if (!changeFranchiseToDefault) return;
      }

      setLoadings((current) => ({ ...current, screen: true }));
      const addNewProduct = [...products];

      const lastProduct = addNewProduct[products.length - 1];

      const required = [
        { value: lastProduct.IdFamilia, key: "IdFamilia" },
        { value: lastProduct.IdProduto, key: "IdProduto" },
        { value: lastProduct.IdVersao, key: "IdVersao" },
      ];

      if (
        lastProduct.IdVersao !== GlobalVariables.ID_VERSAO_ERP_PRO_ADICIONAL && lastProduct.Trial
      ) {
        required.push({
          value: lastProduct.DataExpiracao,
          key: "DataExpiracao",
        });
      }

      let validationFail = false;

      let resultValidation = validatesNotNull(required);

      if (user.IdTipoUsuario !== GlobalVariables.ADM_QYON) {
        lastProduct.IdTipoPromotor = user.IdTipoPromotor;
        lastProduct.PromotorCNPJ = user.PromotorCNPJ;
        lastProduct.IdUsuario = user.Id;
      }

      if (
        lastProduct.Trial &&
        (Object.prototype.toString.call(new Date(lastProduct.DataExpiracao)) !==
          "[object Date]" ||
          isNaN(new Date(lastProduct.DataExpiracao).getTime()))
      ) {
        notify("A data de expiração informada é inválida.");
        validationFail = true;
        resultValidation.errorsValidation.DataExpiracao = true;
      }

      if (resultValidation.error || validationFail) {
        setErrors({
          ...resultValidation.errorsValidation,
          index: products.length - 1,
        });

        return;
      } else {
        setErrors({});
      }

      const cnpjRemoveMask = cnpj;

      const body = {
        IdTipoUsuarioLogin: parseInt(user.IdTipoUsuario),
        Data: new Date(),
        IdTipoPromotor: lastProduct.IdTipoPromotor
          ? parseInt(lastProduct.IdTipoPromotor)
          : null,
        PromotorCNPJ: lastProduct.PromotorCNPJ || null,
        IdUsuario: lastProduct.IdUsuario
          ? parseInt(lastProduct.IdUsuario)
          : null,
        IdFamilia: parseInt(lastProduct.IdFamilia),
        IdProduto: parseInt(lastProduct.IdProduto),
        IdVersao: parseInt(lastProduct.IdVersao),
        Rota: lastProduct?.Rota,
        ClienteCNPJ: cnpjRemoveMask.replace(/[^0-9]+/g, ""),
        Fantasia: fantasy,
        Email: email,
        ValoresParametros: lastProduct.ValoresParametros,
        Demo: lastProduct.Demo,
        Trial: lastProduct.Trial,
        Freemium: lastProduct.Freemium,
        DataExpiracao: lastProduct.Trial ? lastProduct.DataExpiracao : null,
        GenerateBase: generateBase,
        Volumetrias: lastProduct.Volumetrias || [],
      };
      const result = await api.post("mapa", body);

      if (result) {
        postLog({
          EmailUsuario: user?.Email,
          Descricao: `cadastro de produto idfamilia(${lastProduct.IdFamilia
            }) cliente(${cnpjRemoveMask.replace(/[^0-9]+/g, "")})`,
          Origem: "plug",
          Tabela: "mapas",
          Dados: JSON.stringify(body),
        });

        if (
          (result?.data?.data?.Volumetrias || []).filter((v) => v?.success)
            .length !== lastProduct.Volumetrias.length
        ) {
          notify(
            "Não foi possível criar todas as volumetrias",
            true,
            "warning"
          );
        }

        addNewProduct[products.length - 1].Id = result.data.data.IdMapa;
        addNewProduct[products.length - 1].IdTipoPromotor =
          lastProduct.IdTipoPromotor;
        addNewProduct[products.length - 1].PromotorCNPJ =
          lastProduct.PromotorCNPJ;
        addNewProduct[products.length - 1].IdUsuario = lastProduct.IdUsuario;
        addNewProduct[products.length - 1].New = true;

        addNewProduct.push({
          IdFamilia: 0,
          IdProduto: 0,
          IdVersao: 0,
          Rota: null,
          DataExpiracao: "",
          DataCancelamento: "",
          DataBloqueio: "",
          DataConsultaLimite: "",
          Demo: false,
          Trial: false,
          Freemium: false,
          ValoresParametros: {},
          IdMotivoCancelamento: 0,
        });

        setProducts(addNewProduct);
        notify("Produto adicionado com sucesso!", true, "success");
      }
    } catch (error) {
      console.error(error);
      notify("Não foi possível adicionar o produto.");
    } finally {
      setLoadings((current) => ({ ...current, screen: false }));
    }
  };

  const handleEditProduct = async (index) => {
    try {
      setLoadings((current) => ({ ...current, screen: true }));

      const copyProducts = [...products];

      const currentProduct = copyProducts[index];

      const required = [
        { value: currentProduct.IdFamilia, key: "IdFamilia" },
        { value: currentProduct.IdProduto, key: "IdProduto" },
        { value: currentProduct.IdVersao, key: "IdVersao" },
      ];

      if (
        currentProduct.IdVersao !== GlobalVariables.ID_VERSAO_ERP_PRO_ADICIONAL
      ) {
        required.push({ value: currentProduct.Rota, key: "Rota" });

        if (productIsTrial(currentProduct)) {
          required.push({
            value: currentProduct.DataExpiracao,
            key: "DataExpiracao",
          });
        }
      }

      if (currentProduct.IdMotivoCancelamento) {
        required.push(
          {
            value: currentProduct.DataBloqueio,
            key: "DataBloqueio",
          },
          {
            value: currentProduct.DataConsultaLimite,
            key: "DataConsultaLimite",
          }
        );
      }

      let validationFail = false;

      let resultValidation = validatesNotNull(required);

      if (
        currentProduct.Trial &&
        (Object.prototype.toString.call(
          new Date(currentProduct.DataExpiracao)
        ) !== "[object Date]" ||
          isNaN(new Date(currentProduct.DataExpiracao).getTime()))
      ) {
        notify("A data de expiração informada é inválida.");
        validationFail = true;
        resultValidation.errorsValidation.DataExpiracao = true;
      }

      if (currentProduct.IdMotivoCancelamento) {
        if (
          Object.prototype.toString.call(
            new Date(currentProduct.DataBloqueio)
          ) !== "[object Date]" ||
          isNaN(new Date(currentProduct.DataBloqueio).getTime())
        ) {
          notify("A data do bloqueio informada é inválida.");
          validationFail = true;
          resultValidation.errorsValidation.DataBloqueio = true;
        }

        if (
          Object.prototype.toString.call(
            new Date(currentProduct.DataConsultaLimite)
          ) !== "[object Date]" ||
          isNaN(new Date(currentProduct.DataConsultaLimite).getTime())
        ) {
          notify("A data de limite de consulta informada é inválida.");
          validationFail = true;
          resultValidation.errorsValidation.DataConsultaLimite = true;
        }
      }

      if ((currentProduct?.Rota || "").length < 2) {
        notify("O nome de acesso deve conter no mínimo 2 caracteres.");
        validationFail = true;
        resultValidation.errorsValidation.Rota = true;
      }

      if (resultValidation.error || validationFail) {
        setErrors({ ...resultValidation.errorsValidation, index });
        return;
      } else {
        setErrors({});
      }

      const cnpjRemoveMask = cnpj;

      const body = {
        DataBloqueio: currentProduct.IdMotivoCancelamento
          ? currentProduct.DataBloqueio
          : null,
        DataConsultaLimite: currentProduct.IdMotivoCancelamento
          ? currentProduct.DataConsultaLimite
          : null,

        IdMotivoCancelamento: currentProduct.IdMotivoCancelamento || null,
        DataCancelamento: currentProduct.DataCancelamento || null,
        IdFamilia: currentProduct.IdFamilia,
        IdProduto: currentProduct.IdProduto,
        IdVersao: currentProduct.IdVersao,
        Rota: currentProduct.Rota,
        ClienteCNPJ: cnpjRemoveMask.replace(/[^0-9]+/g, ""),
        ValoresParametros: currentProduct.ValoresParametros,
        Demo: currentProduct.Demo,
        Trial: currentProduct.Trial,
        Freemium: currentProduct.Freemium,
        DataExpiracao: currentProduct.DataExpiracao,
        Volumetrias: currentProduct.Volumetrias || [],
        Tester: currentProduct.Tester,
      };
      const { data } = await api.put(`mapa/${currentProduct.Id}`, body);

      if (data.success && data.data) {
        postLog({
          EmailUsuario: user?.Email,
          Descricao: `edição de produto idfamilia(${currentProduct.IdFamilia
            }) cliente(${cnpjRemoveMask.replace(/[^0-9]+/g, "")})`,
          Origem: "plug",
          Tabela: "mapas",
          Dados: JSON.stringify(body),
        });

        currentProduct.ValoresParametros = data.data.ValoresParametros;
        copyProducts[index] = currentProduct;
        setProducts(copyProducts);
        notify(data.msg, true, "info");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadings((current) => ({ ...current, screen: false }));
    }
  };

  const handleChangeItem = ({ item }) => {
    let paramsCurrent = [];
    if (item.IdVersao) {
      const currentVersion = optionsVersions.find(
        (e) => e.Id === item.IdVersao
      );

      if (currentVersion && currentVersion.Parametros) {
        try {
          const jsonVol = JSON.parse(currentVersion.Parametros);
          paramsCurrent = jsonVol.params;
        } catch (error) {
          item.IdVersao = 0;
          notify("Não foi possível exibir parâmetros do produto.");
        }
      }
    }

    return paramsCurrent;
  };

  const productIsTrial = (item) => {
    return item.Trial && item.Trial !== "N";
  };

  const boolChangeToDefaultFranchise = async () => {
    const swalCallback = await questionDefaultFranchise();

    if (swalCallback)
      await handleSavePromocode({
        defaultFranchiseData: {
          CNPJ: GlobalVariables.ID_PROMOTOR_QYON,
          Fantasia: "QYON",
          Razao: "QYON BRASIL",
          IdStatus: 1,
          IdTipoPromotor: 2,
        },
      });
    else notify("Não foi possível salvar produto sem vínculo da franquia");

    return swalCallback;
  };

  const handleCurrentBoxAndUncheckOthers = (currentBox, item) => {
    const avaliableBoxes = ["Demo", "Freemium", "Trial", "Tester"];

    item[currentBox] = !item[currentBox];

    if (item[currentBox])
      avaliableBoxes
        .filter((box) => box !== currentBox)
        .forEach((filteredBox) => {
          item[filteredBox] = false;
        });

    return item;
  };

  return {
    handleAddProduct,
    handleEditProduct,
    handleChangeItem,
    productIsTrial,
    handleCurrentBoxAndUncheckOthers,
  };
}
